/**
 * GapsPrimitives.js
 * Tiny, reusable UI atoms used across the gaps feature.
 */
import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// ── Animated SVG score ring ──────────────────────────────────────────────────
export function ScoreRing({ value, label, sub, color = '#6366f1', size = 100 }) {
    const r = (size - 14) / 2;
    const circ = 2 * Math.PI * r;
    const dash = circ * (Math.min(value, 100) / 100);

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <svg width={size} height={size}>
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke={alpha(color, 0.14)} strokeWidth={11}
                    />
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke={color} strokeWidth={11}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        style={{ transition: 'stroke-dasharray 0.9s cubic-bezier(.4,0,.2,1)' }}
                    />
                </svg>
                <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Typography sx={{
                        fontWeight: 900,
                        fontSize: size > 90 ? '1.5rem' : '1.05rem',
                        color, lineHeight: 1,
                    }}>
                        {value}
                    </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{
                display: 'block', fontWeight: 700,
                color: 'text.primary', mt: 0.75, fontSize: '0.78rem',
            }}>
                {label}
            </Typography>
            {sub && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.7rem' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}

// ── Single signal comparison row ─────────────────────────────────────────────
export function SignalRow({ label, yours, theirs }) {
    const Tick = ({ active }) => active
        ? <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981' }} />
        : <CancelIcon sx={{ fontSize: 20, color: alpha('#9ca3af', 0.5) }} />;

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 1,
            py: 0.6,
        }}>
            {/* Your signal — right-aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tick active={yours} />
            </Box>

            {/* Label — centred */}
            <Typography variant="caption" sx={{
                fontWeight: 600, color: 'text.secondary',
                fontSize: '0.78rem', whiteSpace: 'nowrap', textAlign: 'center',
                minWidth: 120,
            }}>
                {label}
            </Typography>

            {/* Competitor signal — left-aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Tick active={theirs} />
            </Box>
        </Box>
    );
}
